import { useState } from "react";
import { useGetOne, useNotify, useRefresh, useUpdate } from "ra-core";
import { Ban } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { Company } from "../types";

// LDF: Kontaktsperre – diese Firma wird unter keinen Umständen kontaktiert (E-Mail, Telefon, Brief, Akquise).

export const ContactBlockBanner = ({ company }: { company?: Pick<Company, "contact_blocked" | "contact_block_reason" | "contact_blocked_at"> | null }) => {
  if (!company?.contact_blocked) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border-2 border-red-600 bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950 dark:text-red-100">
      <Ban className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <strong>Kontaktsperre – nicht kontaktieren.</strong> Keine E-Mails, Anrufe, Briefe oder Akquise.
        {company.contact_block_reason && <div className="text-xs">Grund: {company.contact_block_reason}</div>}
        {company.contact_blocked_at && (
          <div className="text-xs">Seit {new Date(company.contact_blocked_at).toLocaleDateString("de-DE")}</div>
        )}
      </div>
    </div>
  );
};

// Sperrstatus der Firma eines Kontakts
export const useCompanyBlock = (companyId?: number | string | null) => {
  const { data } = useGetOne<Company>("companies", { id: companyId! }, { enabled: companyId != null });
  return data?.contact_blocked ? data : null;
};

export const ContactBlockButton = ({ record }: { record: Company }) => {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");

  const save = (blocked: boolean) =>
    update(
      "companies",
      {
        id: record.id,
        data: { contact_blocked: blocked, contact_block_reason: blocked ? reason.trim() || null : null },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(blocked ? "Kontaktsperre aktiv – offene Aufgaben wurden geschlossen" : "Kontaktsperre aufgehoben", { type: "success" });
          setAsking(false);
          setReason("");
          refresh();
        },
      },
    );

  if (record.contact_blocked) {
    return (
      <Button size="sm" variant="outline" disabled={isPending} onClick={() => confirm("Kontaktsperre wirklich aufheben?") && save(false)}>
        Sperre aufheben
      </Button>
    );
  }
  if (!asking) {
    return (
      <Button size="sm" variant="outline" className="gap-1 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setAsking(true)}>
        <Ban className="h-3.5 w-3.5" /> Kontaktsperre
      </Button>
    );
  }
  return (
    <div className="w-full space-y-2 rounded-md border border-red-300 p-2">
      <input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Grund (optional), z. B. Keine Zusammenarbeit"
        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
      />
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" disabled={isPending} onClick={() => save(true)}>
          Sperren
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setAsking(false)}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
};
